import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { useLeads } from '../context/LeadsContext';
import { useAuth } from '../context/AuthContext';

// ── Chave de telefone ─────────────────────────────────────────────────────────
// O mesmo salão aparece como 41 3333-4444, 554133334444 e 5541933334444.
// Sem normalizar, o lead já disparado voltava como "novo" no remarketing.
// Regra: tira o 55, corta pra 11 dígitos e descarta o 9 extra do celular.
const chaveTel = (v) => {
  let d = String(v ?? '').replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('55') && d.length >= 12) d = d.slice(2);
  if (d.length > 11) d = d.slice(-11);
  if (d.length < 10) return '';
  const ddd = d.slice(0, 2);
  let resto = d.slice(2);
  if (resto.length === 9 && resto[0] === '9') resto = resto.slice(1);
  return ddd + resto;
};

const semAcento = (v) => String(v ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Quando a extração varreu vários bairros de uma vez, tenta achar no endereço
// qual deles é o do lead. Se não der, fica em branco em vez de chutar.
const bairroDoLead = (lead, bairros) => {
  if (!bairros.length) return '';
  if (bairros.length === 1) return bairros[0];
  const end = semAcento(lead.endereco);
  return bairros.find((b) => b && end.includes(semAcento(b))) || '';
};

// ── Exportação CSV ────────────────────────────────────────────────────────────
// Três defeitos que faziam o Excel abrir tudo embolado:
//  1. separador vírgula — o Excel em português usa PONTO E VÍRGULA como
//     separador de lista, então jogava a linha inteira na coluna A;
//  2. sem BOM — sem esses 3 bytes o Excel assume Windows-1252 e "Salão"
//     virava "SalÃ£o";
//  3. aspas dentro do texto não eram dobradas, quebrando as colunas da linha.
// Também usa CRLF, que é o fim de linha que o Excel espera.
const baixarCSVExcel = (nomeArquivo, cabecalho, linhas) => {
  const celula = (v) => {
    const s = String(v ?? '').replace(/\r?\n/g, ' ').trim();
    return '"' + s.replace(/"/g, '""') + '"';
  };
  const corpo = [cabecalho, ...linhas].map((l) => l.map(celula).join(';')).join('\r\n');
  const blob = new Blob(['\uFEFF' + corpo], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

export default function MeusLeads() {
  const [extracoes, setExtracoes] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [erro, setErro]           = useState('');
  const [selected, setSelected]   = useState(() => new Set()); // place_ids selecionados
  const [abertos, setAbertos]     = useState(() => new Set()); // extrações expandidas
  const [disparados, setDisparados] = useState(() => new Map()); // chaveTel -> histórico de disparo
  const [filtroDisparo, setFiltroDisparo] = useState('todos');   // todos | novos | disparados
  const [filtroBairro, setFiltroBairro]   = useState('');

  const { setParaKanban, setParaDisparo } = useLeads();
  const { user, contaUid } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && contaUid) {
      carregar(contaUid);
    } else {
      setExtracoes([]); setLoading(false);
      setErro('Você precisa estar logado para ver seus leads.');
    }
    // eslint-disable-next-line
  }, [user, contaUid]);

  // ── Quem já entrou em algum disparo ─────────────────────────────────────────
  // Lê os agendamentos e monta um índice telefone -> { vezes, último disparo }.
  // O blocosJson guarda TODO mundo que entrou na lista, inclusive quem ainda
  // está na fila — que é exatamente o que não deve voltar pro remarketing.
  const carregarDisparos = async (uid) => {
    const mapa = new Map();
    try {
      const q = query(collection(db, 'users', uid, 'agendamentos'), orderBy('criadoEm', 'desc'), limit(80));
      const snap = await getDocs(q);
      snap.forEach((ds) => {
        const d = ds.data();
        let blocos = [];
        try { blocos = JSON.parse(d.blocosJson || '[]'); } catch (e) { blocos = []; }
        const criado = d.criadoEm?.toDate ? d.criadoEm.toDate().toISOString() : d.criadoEm;
        const info = {
          titulo: d.titulo || 'Disparo',
          quando: String(d.inicio || criado || '').slice(0, 10),
          status: d.status || '',
        };
        blocos.flat().forEach((c) => {
          const k = chaveTel(c && c.telefone);
          if (!k) return;
          const atual = mapa.get(k);
          // a query vem do mais novo pro mais antigo: o primeiro visto é o último disparo
          if (!atual) mapa.set(k, { vezes: 1, ultimo: info, titulos: [info.titulo] });
          else {
            atual.vezes += 1;
            if (!atual.titulos.includes(info.titulo)) atual.titulos.push(info.titulo);
          }
        });
      });
    } catch (e) { /* histórico é complementar: falha não pode esconder os leads */ }
    return mapa;
  };

  const carregar = async (uid) => {
    setLoading(true); setErro('');
    try {
      const ref  = collection(db, 'users', uid, 'extracoes');
      const q    = query(ref, orderBy('extraidoEm', 'desc'));
      const [snap, mapaDisparos] = await Promise.all([getDocs(q), carregarDisparos(uid)]);

      const lista = [];
      snap.forEach((docSnap) => {
        const d = docSnap.data();
        let leadsBrutos = [];
        try { leadsBrutos = JSON.parse(d.leadsJson || '[]'); } catch (e) { leadsBrutos = []; }

        // o extrator grava 'bairro' quando foi por bairro, ou 'bairrosVarridos' na varredura
        let bairros = [];
        try { bairros = JSON.parse(d.bairrosVarridos || '[]'); } catch (e) { bairros = []; }
        if (d.bairro) bairros = [d.bairro];
        bairros = bairros.filter(Boolean);

        const leads = leadsBrutos.map((l) => ({
          ...l,
          bairro: l.bairro || bairroDoLead(l, bairros),
          chave:  chaveTel(l.telefone_fmt || l.telefone),
        }));

        lista.push({
          id:         docSnap.id,
          nicho:      d.nicho || '',
          cidade:     d.cidade || '',
          bairros,
          extraidoEm: d.extraidoEm || '',
          leads,
          total:      leads.length,
        });
      });

      const validas = lista.filter((e) => e.total > 0);
      setDisparados(mapaDisparos);
      setExtracoes(validas);
      if (validas.length) setAbertos(new Set([validas[0].id]));
    } catch (e) {
      setErro('Não consegui carregar seus leads: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const infoDisparo = (lead) => (lead.chave ? disparados.get(lead.chave) : null) || null;

  // seleção
  const toggleLead = (pid) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(pid) ? n.delete(pid) : n.add(pid);
      return n;
    });

  const toggleExtracao = (leads) => {
    const pids = leads.map((l) => l.place_id);
    const todosMarcados = pids.every((p) => selected.has(p));
    setSelected((prev) => {
      const n = new Set(prev);
      pids.forEach((p) => (todosMarcados ? n.delete(p) : n.add(p)));
      return n;
    });
  };

  const toggleAberto = (id) =>
    setAbertos((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const fmtData = (iso) => {
    if (!iso || iso.length < 10) return '';
    const [y, m, d] = iso.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
  };

  // ── filtros ──
  const bairrosDisponiveis = useMemo(() => {
    const s = new Set();
    extracoes.forEach((e) => {
      (e.bairros || []).forEach((b) => s.add(b));
      e.leads.forEach((l) => l.bairro && s.add(l.bairro));
    });
    return [...s].sort((a, b) => String(a).localeCompare(String(b), 'pt-BR'));
  }, [extracoes]);

  const extracoesVisiveis = useMemo(() => {
    const passa = (l) => {
      if (filtroBairro && (l.bairro || '') !== filtroBairro) return false;
      const d = l.chave ? disparados.get(l.chave) : null;
      if (filtroDisparo === 'novos' && d) return false;
      if (filtroDisparo === 'disparados' && !d) return false;
      return true;
    };
    return extracoes
      .map((e) => ({ ...e, visiveis: e.leads.filter(passa) }))
      .filter((e) => e.visiveis.length > 0);
  }, [extracoes, disparados, filtroDisparo, filtroBairro]);

  const todosLeads   = extracoes.flatMap((e) => e.leads);
  const leadsVisiveis = extracoesVisiveis.flatMap((e) => e.visiveis);
  const jaDisparados = todosLeads.filter((l) => l.chave && disparados.has(l.chave)).length;
  const selecionados = todosLeads.filter((l) => selected.has(l.place_id));

  const selecionarVisiveis = (somenteNovos) => {
    const alvo = leadsVisiveis.filter((l) => !somenteNovos || !(l.chave && disparados.has(l.chave)));
    setSelected(new Set(alvo.map((l) => l.place_id)));
  };

  // ── ações (pontes) ──
  const irParaKanban = () => {
    if (!selecionados.length) return;
    setParaKanban(selecionados);
    navigate('/kanban');
  };
  const irParaDisparo = () => {
    if (!selecionados.length) return;
    setParaDisparo(selecionados);
    navigate('/disparo');
  };

  const baixarCSV = () => {
    const fonte = selecionados.length ? selecionados : leadsVisiveis;
    if (!fonte.length) return;
    baixarCSVExcel('meus_leads.csv',
      ['Nome', 'Telefone', 'Tipo', 'Bairro', 'Endereço', 'Nota', 'Site', 'Já disparado', 'Último disparo', 'Campanha'],
      fonte.map((l) => {
        const d = infoDisparo(l);
        return [l.nome, l.telefone, l.tipo, l.bairro || '', l.endereco, l.nota, l.site || '',
          d ? `Sim (${d.vezes}x)` : 'Não', d ? fmtData(d.ultimo.quando) : '', d ? d.titulos.join(' | ') : ''];
      }));
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: '26px',
          fontWeight: '800', letterSpacing: '-0.5px', marginBottom: '6px' }}>
          Meus Leads
        </h1>
        <p style={{ color: '#52526e', fontSize: '13px' }}>
          Todos os leads que você já extraiu, salvos e organizados por busca.
        </p>
      </div>

      {loading && (
        <div style={{ ...card, textAlign: 'center', color: '#9898b8', fontSize: '13px' }}>
          Carregando seus leads...
        </div>
      )}

      {!loading && erro && (
        <div style={{ ...card, color: '#dc2626', fontSize: '13px',
          background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.2)' }}>
          ⚠ {erro}
        </div>
      )}

      {!loading && !erro && extracoes.length === 0 && (
        <div style={{ ...card, textAlign: 'center', padding: '40px 24px' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>📭</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700',
            fontSize: '15px', marginBottom: '6px' }}>
            Nenhum lead salvo ainda
          </div>
          <div style={{ color: '#9898b8', fontSize: '13px' }}>
            Use o <strong>Extrator Maps</strong> para gerar seus primeiros leads —
            eles aparecem aqui automaticamente.
          </div>
        </div>
      )}

      {/* Barra de ação */}
      {!loading && !erro && extracoes.length > 0 && (
        <div style={{ ...card, padding: '14px 20px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ fontSize: '13px', color: '#52526e' }}>
            <strong style={{ color: '#0a0a18' }}>{todosLeads.length}</strong> leads salvos
            <span style={{ color: '#9898b8', marginLeft: '8px' }}>
              · {jaDisparados} já disparados · {todosLeads.length - jaDisparados} nunca disparados
            </span>
            {selecionados.length > 0 && (
              <span style={{ color: '#6c47ff', marginLeft: '10px', fontWeight: '600' }}>
                · {selecionados.length} selecionados
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={baixarCSV} style={btnGhost}>
              {selecionados.length ? `↓ CSV (${selecionados.length})` : `↓ CSV (${leadsVisiveis.length})`}
            </button>
            {selecionados.length > 0 && (
              <>
                <button onClick={irParaKanban} style={btnGhost}>
                  ＋ Criar card no Kanban
                </button>
                <button onClick={irParaDisparo} style={btnPrimary}>
                  🚀 Disparar {selecionados.length} →
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Filtros: é aqui que o remarketing acontece */}
      {!loading && !erro && extracoes.length > 0 && (
        <div style={{ ...card, padding: '14px 20px', marginBottom: '16px',
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', color: '#9898b8', fontFamily: 'monospace',
            textTransform: 'uppercase', letterSpacing: '0.06em' }}>Mostrar</span>
          <div style={{ display: 'flex', gap: '4px', background: '#f0f0f7', padding: '3px', borderRadius: '9px' }}>
            {[['todos', 'Todos'], ['novos', '🆕 Nunca disparados'], ['disparados', '🔁 Já disparados']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setFiltroDisparo(v)} style={{
                background: filtroDisparo === v ? '#fff' : 'transparent',
                color: filtroDisparo === v ? '#0a0a18' : '#52526e',
                border: 'none', borderRadius: '7px', padding: '6px 12px', fontSize: '12px',
                fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                boxShadow: filtroDisparo === v ? '0 1px 2px rgba(0,0,0,0.08)' : 'none' }}>{l}</button>
            ))}
          </div>

          {bairrosDisponiveis.length > 0 && (
            <select value={filtroBairro} onChange={(e) => setFiltroBairro(e.target.value)}
              style={{ border: '1.5px solid rgba(0,0,0,0.1)', borderRadius: '8px', padding: '7px 10px',
                fontSize: '12px', background: '#f8f8fc', cursor: 'pointer', color: '#52526e',
                fontFamily: 'DM Sans, sans-serif' }}>
              <option value="">Todos os bairros</option>
              {bairrosDisponiveis.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={() => selecionarVisiveis(true)} style={btnGhost}>Selecionar só os novos</button>
            <button onClick={() => selecionarVisiveis(false)} style={btnGhost}>Selecionar tudo da lista</button>
            {selected.size > 0 && <button onClick={() => setSelected(new Set())} style={btnGhost}>Limpar</button>}
          </div>

          {(filtroDisparo !== 'todos' || filtroBairro) && (
            <div style={{ width: '100%', fontSize: '12px', color: '#52526e' }}>
              Mostrando <strong>{leadsVisiveis.length}</strong> de {todosLeads.length} leads.
              <button onClick={() => { setFiltroDisparo('todos'); setFiltroBairro(''); }}
                style={{ ...btnGhost, padding: '3px 9px', marginLeft: '8px' }}>✕ Tirar filtros</button>
            </div>
          )}
        </div>
      )}

      {!loading && !erro && extracoes.length > 0 && extracoesVisiveis.length === 0 && (
        <div style={{ ...card, textAlign: 'center', color: '#9898b8', fontSize: '13px' }}>
          Nenhum lead com esses filtros.
        </div>
      )}

      {/* Lista de extrações */}
      {!loading && !erro && extracoesVisiveis.map((ex) => {
        const aberto = abertos.has(ex.id);
        const pids = ex.visiveis.map((l) => l.place_id);
        const todosMarcados = pids.length > 0 && pids.every((p) => selected.has(p));
        return (
          <div key={ex.id} style={{ ...card, padding: 0, overflow: 'hidden', marginBottom: '12px' }}>
            <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', cursor: 'pointer',
              borderBottom: aberto ? '1px solid rgba(0,0,0,0.07)' : 'none' }}
              onClick={() => toggleAberto(ex.id)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '12px', color: '#9898b8',
                  transform: aberto ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</span>
                <div>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '14px',
                    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span>
                      {ex.nicho || 'Extração'} {ex.cidade && <span style={{ color: '#9898b8' }}>· {ex.cidade}</span>}
                    </span>
                    {(ex.bairros || []).map((b) => (
                      <span key={b} style={{ fontSize: '10px', fontWeight: '700', fontFamily: 'monospace',
                        background: 'rgba(5,150,105,0.1)', color: '#047857', borderRadius: '6px', padding: '2px 8px' }}>
                        📍 {b}
                      </span>
                    ))}
                  </div>
                  <div style={{ fontSize: '11px', color: '#9898b8', fontFamily: 'monospace', marginTop: '2px' }}>
                    {fmtData(ex.extraidoEm)} · {ex.total} leads
                    {ex.visiveis.length !== ex.total && ` · ${ex.visiveis.length} no filtro`}
                  </div>
                </div>
              </div>
              <span style={{ fontFamily: 'monospace', fontSize: '11px',
                background: 'rgba(108,71,255,0.08)', color: '#6c47ff',
                borderRadius: '6px', padding: '2px 9px' }}>
                {ex.visiveis.length !== ex.total ? `${ex.visiveis.length}/${ex.total}` : ex.total}
              </span>
            </div>

            {aberto && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f8f8fc' }}>
                    <th style={th}>
                      <input type="checkbox" checked={todosMarcados}
                        onChange={() => toggleExtracao(ex.visiveis)} />
                    </th>
                    <th style={th}>Nome</th>
                    <th style={th}>Telefone</th>
                    <th style={th}>Tipo</th>
                    <th style={th}>Bairro</th>
                    <th style={th}>Endereço</th>
                    <th style={th}>Nota</th>
                    <th style={th}>Disparo</th>
                  </tr>
                </thead>
                <tbody>
                  {ex.visiveis.map((l, i) => (
                    <tr key={l.place_id || i} style={{
                      borderTop: '1px solid rgba(0,0,0,0.05)',
                      background: selected.has(l.place_id) ? 'rgba(108,71,255,0.03)' : 'transparent' }}>
                      <td style={td}>
                        <input type="checkbox" checked={selected.has(l.place_id)}
                          onChange={() => toggleLead(l.place_id)} />
                      </td>
                      <td style={{ ...td, fontWeight: '500', maxWidth: '180px',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.nome}
                      </td>
                      <td style={{ ...td, fontFamily: 'monospace' }}>{l.telefone}</td>
                      <td style={td}>
                        <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '600',
                          background: l.tipo === 'Celular' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                          color: l.tipo === 'Celular' ? '#059669' : '#d97706' }}>{l.tipo}</span>
                      </td>
                      <td style={{ ...td, color: '#52526e', maxWidth: '120px',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.bairro || <span style={{ color: '#c4c4d4' }}>—</span>}
                      </td>
                      <td style={{ ...td, color: '#52526e', maxWidth: '200px',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.endereco}
                      </td>
                      <td style={td}>{'⭐ ' + l.nota}</td>
                      <td style={td}>
                        {(() => {
                          const d = infoDisparo(l);
                          if (!d) return <span style={{ color: '#c4c4d4', fontSize: '11px' }}>nunca</span>;
                          return (
                            <span title={d.titulos.join(' · ')}
                              style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '600',
                                background: 'rgba(108,71,255,0.1)', color: '#6c47ff', whiteSpace: 'nowrap' }}>
                              ✔ {fmtData(d.ultimo.quando) || 'enviado'}{d.vezes > 1 ? ` · ${d.vezes}x` : ''}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

const card = { background: '#fff', border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const btnGhost = { border: '1.5px solid rgba(0,0,0,0.1)', background: 'none',
  borderRadius: '8px', padding: '7px 14px', fontSize: '12px',
  cursor: 'pointer', color: '#52526e', fontFamily: 'DM Sans, sans-serif' };
const btnPrimary = { background: 'linear-gradient(135deg, #6c47ff, #5535e0)',
  color: '#fff', border: 'none', borderRadius: '8px', padding: '7px 16px',
  fontSize: '12px', fontWeight: '600', cursor: 'pointer' };
const th = { padding: '10px 14px', textAlign: 'left', fontSize: '10px',
  fontWeight: '700', color: '#9898b8', textTransform: 'uppercase',
  letterSpacing: '0.06em', fontFamily: 'monospace' };
const td = { padding: '11px 14px' };
